import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, AlertCircle, Info, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

type AlertSeverity = 'info' | 'warning' | 'critical';

export interface DashboardAlert {
  id: string;
  severity: AlertSeverity;
  message: string;
  createdAt: string;
  link: string;
}

interface AlertsPanelProps {
  alerts: DashboardAlert[];
  isLoading?: boolean;
  className?: string;
  maxItems?: number;
}

const severityConfig = {
  info: {
    icon: Info,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    badgeVariant: 'secondary' as const,
    dotColor: 'bg-blue-500',
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    badgeVariant: 'outline' as const,
    dotColor: 'bg-amber-500',
  },
  critical: {
    icon: AlertCircle,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    badgeVariant: 'destructive' as const,
    dotColor: 'bg-red-500',
  },
};

export function AlertsPanel({ alerts, isLoading = false, className, maxItems = 5 }: AlertsPanelProps) {
  const navigate = useNavigate();
  const displayedAlerts = alerts.slice(0, maxItems);
  const hasMore = alerts.length > maxItems;

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Alerts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
              <div className="w-5 h-5 bg-muted animate-pulse rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (alerts.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Info className="h-5 w-5 mr-2" />
            <span>No active alerts</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Alerts</CardTitle>
          <Badge variant="secondary">{alerts.length}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayedAlerts.map((alert) => {
          const config = severityConfig[alert.severity];
          
          return (
            <div
              key={alert.id}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-muted/50',
                config.borderColor
              )}
              onClick={() => navigate(alert.link)}
            >
              <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', config.dotColor)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{alert.message}</p>
              </div>
              <Badge variant={config.badgeVariant} className="shrink-0 capitalize text-xs">
                {alert.severity}
              </Badge>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>
          );
        })}
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => navigate('/alerts')}
          >
            View All Alerts ({alerts.length})
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default AlertsPanel;
